/**
 * Base Service
 * Generic service with error handling, retry logic, and type safety
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { ApiResponse, ApiError } from '../types'
import { normalizeError, logError } from '../utils/error.utils'
import { API_CONFIG, STORAGE_KEYS, ROUTE_PATHS } from '../constants/app.constants'
import { refreshAccessToken, hasRefreshToken } from '../utils/refresh-access-token'

const EXP_SKEW_SECONDS = 60

/** True if the JWT is missing/undecodable/expiring within `EXP_SKEW_SECONDS`. */
function isExpiringSoon(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token)
    if (!exp) return true
    return exp * 1000 - Date.now() < EXP_SKEW_SECONDS * 1000
  } catch {
    return true
  }
}

export class BaseService {
  protected client: AxiosInstance

  constructor(baseURL: string = API_CONFIG.BASE_URL) {
    this.client = axios.create({
      baseURL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        let token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)

        // Proactive refresh: missing or expiring within 60s → exchange the
        // refresh token BEFORE the call goes out. Single-flight with the
        // reactive 401 retry below.
        if (!token || isExpiringSoon(token)) {
          const refreshed = await refreshAccessToken()
          if (refreshed) token = refreshed
        }

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        logError(error, 'Request Interceptor')
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        logError(error, 'Response Interceptor')

        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean
        }

        // Handle 401 errors (unauthorized) — try a silent refresh before
        // giving up on the session, instead of logging out immediately.
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true

          const newToken = await refreshAccessToken()
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return this.client(originalRequest)
          }

          // refreshAccessToken() clears the refresh token itself ONLY on a
          // definitive 401 from the refresh endpoint — a transient failure
          // (network blip, 5xx) leaves it in place. Only force a hard logout
          // when the session is actually confirmed dead; otherwise just let
          // this one request fail and try again next time.
          if (!hasRefreshToken()) {
            localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
            localStorage.removeItem(STORAGE_KEYS.USER_DATA)
            // This app uses HashRouter, so the redirect has to go through
            // the hash, not a bare path.
            window.location.hash = ROUTE_PATHS.LOGIN
          }
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Generic GET request with retry logic
   */
  protected async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    retries: number = API_CONFIG.RETRY_ATTEMPTS
  ): Promise<T> {
    try {
      const response = await this.client.get<ApiResponse<T>>(url, config)
      return this.extractData(response.data)
    } catch (error) {
      if (retries > 0 && this.shouldRetry(error)) {
        await this.delay(API_CONFIG.RETRY_DELAY)
        return this.get<T>(url, config, retries - 1)
      }
      throw this.handleError(error, `GET ${url}`)
    }
  }

  /**
   * Generic POST request
   */
  protected async post<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.post<ApiResponse<T>>(url, data, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `POST ${url}`)
    }
  }

  /**
   * Generic PUT request
   */
  protected async put<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.put<ApiResponse<T>>(url, data, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `PUT ${url}`)
    }
  }

  /**
   * Generic PATCH request
   */
  protected async patch<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.patch<ApiResponse<T>>(url, data, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `PATCH ${url}`)
    }
  }

  /**
   * Generic DELETE request
   */
  protected async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.delete<ApiResponse<T>>(url, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `DELETE ${url}`)
    }
  }

  /**
   * Extract data from API response
   */
  private extractData<T>(response: any): T {
    // Handle different response formats
    if (response && typeof response === 'object') {
      if ('data' in response) {
        return response.data
      }
      return response
    }
    return response
  }

  /**
   * Handle and normalize errors
   */
  private handleError(error: unknown, context: string): ApiError {
    const normalized = normalizeError(error)
    logError(error, context)
    return normalized
  }

  /**
   * Check if error should trigger a retry
   */
  private shouldRetry(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status
      // Retry on network errors or 5xx server errors
      return !status || status >= 500
    }
    return false
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Build query string from parameters
   */
  protected buildQueryString(params: Record<string, any>): string {
    const queryParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value))
      }
    })
    
    const queryString = queryParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

